import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect } from "react";
import styled, { useTheme } from "styled-components";
import Button from "~/components-shared/Button";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { usePostHog } from "posthog-js/react";
import { yupResolver } from "@hookform/resolvers/yup";
import Card from "~/components-shared/Card";
import InviteMembersForm from "~/forms/InviteMembersForm";
import inviteMembersSchema from "~/forms/InviteMembersForm/inviteMembersSchema";
import Icon from "~/components-shared/Icon";
import { StatusStateEnum, MemberStatusEnum } from "~/types/enums";
import { useDispatch, useSelector } from "react-redux";
import onboardingActionCreators from "~/store/onboarding/actions";
import authSelectors from "~/store/auth/selectors";
import { helpers, onboardingOrder } from "~/lib/helpers";
import { Alert, Box } from "~/components-shared";
import onboardingSelectors from "~/store/onboarding/selectors";
import toastsActionCreators from "~/store/toasts/actions";
import memberSelectors from "~/store/members/selectors";
import membersActionCreators from "~/store/members/actions";
const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;
const Content = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;
const StyledIcon = styled(Icon)`
  box-shadow: inset 0 0 0 6px ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.success.main;
}};
  border-radius: 50%;
`;
const InviteMembersCard = _ref2 => {
  let {
    open,
    ...rest
  } = _ref2;
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const user = useSelector(authSelectors.getUser);
  const roles = useSelector(memberSelectors.getRoles);
  const rolesStatus = useSelector(memberSelectors.getRolesStatus);
  const userRoleWeight = user.role.weight;
  const filteredRoles = roles.filter(_ref3 => {
    let {
      weight
    } = _ref3;
    return weight <= userRoleWeight;
  }).map(_ref4 => {
    let {
      id,
      name,
      description
    } = _ref4;
    return {
      id,
      value: name,
      tip: description
    };
  });
  const success = user?.status && onboardingOrder[user.status] > onboardingOrder[MemberStatusEnum.ONBOARDING_INVITE_MEMBERS];
  const dispatch = useDispatch();
  const defaultValues = {
    invitations: Array(3).fill(1).map(_e => ({
      firstName: "",
      email: "",
      roleId: ""
    }))
  };
  const nextStatus = useSelector(onboardingSelectors.getStatus);
  const backStatus = useSelector(onboardingSelectors.getBackStatus);
  const nextError = useSelector(onboardingSelectors.getError);
  const backError = useSelector(onboardingSelectors.getBackError);
  const availableStatuses = helpers.memberOnboardingStatuses(user.authorities);
  const statusIndex = availableStatuses.findIndex(item => item === user.status);
  const error = nextError || backError;
  const posthog = usePostHog();
  const {
    handleSubmit,
    control,
    setError,
    reset,
    formState: {
      errors
    }
  } = useForm({
    resolver: yupResolver(inviteMembersSchema),
    defaultValues
  });
  useEffect(() => {
    if (rolesStatus === StatusStateEnum.IDLE) {
      dispatch(membersActionCreators.fetchRolesStarted());
    }
  }, [rolesStatus]);
  const onBack = () => {
    dispatch(onboardingActionCreators.backStarted());
    posthog.capture(`Onboarding Member Profile – Back`);
  };
  const onSubmit = handleSubmit(data => {
    const validInvitations = {
      invitations: data.invitations.filter(item => item.firstName && item.email && item.roleId)
    };
    if (!validInvitations.invitations.length) {
      setError("invitations", {
        message: t("InviteMembersCard.required")
      });
      return;
    }
    dispatch(onboardingActionCreators.inviteMembersSubmitStarted(validInvitations, _ref5 => {
      let {
        data: response
      } = _ref5;
      if (response) {
        dispatch(toastsActionCreators.pushed({
          severity: "success",
          message: t("InviteMembersModal.wereInvited"),
          timeout: 10000
        }));
        reset(defaultValues);
      }
    }));
    posthog.capture(`Onboarding Invite Members – Submit`, {
      ...helpers.objectDiff(defaultValues, data)
    });
  });
  const onSkip = () => {
    dispatch(onboardingActionCreators.inviteMembersSkipStarted());
    posthog.capture(`Onboarding Invite Members – Skip`);
  };
  return /*#__PURE__*/React.createElement(Card, _extends({
    s: {
      px: 10,
      py: 6,
      mb: 10
    },
    collapsed: !open,
    elemAfter: success ? /*#__PURE__*/React.createElement(StyledIcon, {
      src: "checkboxFill",
      scale: 8,
      color: colors.success.main
    }) : undefined,
    shadow: true
  }, rest), /*#__PURE__*/React.createElement(Form, {
    onSubmit: onSubmit
  }, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), errors?.invitations?.message && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, errors.invitations.message), /*#__PURE__*/React.createElement(Content, null, /*#__PURE__*/React.createElement(InviteMembersForm, {
    control: control,
    roles: filteredRoles
  })), /*#__PURE__*/React.createElement(Box, {
    direction: "row",
    gap: 5,
    justify: "end",
    s: {
      mt: 10,
      mb: 2
    },
    fullWidth: true
  }, statusIndex !== 0 && /*#__PURE__*/React.createElement(Button, {
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "arrow",
      scale: 5,
      rotate: 180,
      s: {
        mr: 2
      }
    }),
    color: "translucent",
    type: "submit",
    onClick: onBack,
    loading: backStatus === StatusStateEnum.LOADING,
    s: {
      pl: 4,
      pr: 6
    },
    rounded: true
  }, t("common.actions.back")), /*#__PURE__*/React.createElement(Button, {
    onClick: onSkip,
    color: "translucent",
    s: {
      px: 8
    },
    rounded: true
  }, t("common.actions.skip")), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    type: "submit",
    maxWidth: "230px",
    onClick: onSubmit,
    loading: nextStatus === StatusStateEnum.LOADING,
    fullWidth: true,
    rounded: true
  }, t("InviteMembersCard.sendInvites")))));
};
export default InviteMembersCard;